import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { cloneDeep, isEqual } from 'lodash';
import { makeDataState, withStateLoading, withStateSaving } from '@/util/dataState';
import type { AxiosRequestConfig } from 'axios';
import type { SurgicalGuideCaseState } from '@/stores/surgicalGuide/surgicalGuide.types';
import {
    initialCaseState,
    initialSurgicalGuideCaseData,
} from '@/stores/surgicalGuide/surgicalGuideInitialState';
import { caseSettingsToState } from '@/api/surgicalGuide/mapping';
import {
    loadCaseSurgicalGuideSettings,
    saveCaseSettings,
} from '@/api/surgicalGuide/services';
import { getUserPreferences } from '@/api/surgicalGuide/request';
import { isValid as isFormValid, validators } from '@/stores/surgicalGuide/surgicalGuideValidation';

function surgicalGuideCaseState(): SurgicalGuideCaseState {
    return {
        ...cloneDeep(initialCaseState),
    };
}

function resetCaseState(state: SurgicalGuideCaseState): void {
    Object.assign(state.current, initialSurgicalGuideCaseData);
    Object.assign(state.lastSaved, initialSurgicalGuideCaseData);
    Object.assign(state, makeDataState());
}

async function loadNewCase(
    state: SurgicalGuideCaseState,
    surgeonId: number,
    isSingleSurgeon: boolean,
    config?: AxiosRequestConfig,
): Promise<void> {
    resetCaseState(state);
    await withStateLoading(state, async () => {
        // Only inherit surgeon preferences for single-surgeon cases.
        if (isSingleSurgeon) {
            const preferences = await getUserPreferences(surgeonId, config);
            if (preferences) {
                Object.assign(state.current, caseSettingsToState(preferences));
            }
        }
        Object.assign(state.lastSaved, cloneDeep(state.current));
    }, config?.signal);
}

async function loadExistingCase(
    state: SurgicalGuideCaseState,
    id: number,
    config?: AxiosRequestConfig,
): Promise<void> {
    resetCaseState(state);
    await withStateLoading(state, async () => {
        const data = await loadCaseSurgicalGuideSettings(id, config);
        if (data) {
            Object.assign(state.current, data);
        }
        Object.assign(state.lastSaved, cloneDeep(state.current));
    }, config?.signal);
}

async function updateSurgeon(state: SurgicalGuideCaseState, surgeonId: number): Promise<void> {
    // Only the surgical approach follows the surgeon; the CR number is tied to
    // the case, not the surgeon, so it is preserved.
    const preferences = await getUserPreferences(surgeonId);
    if (preferences) {
        state.current.surgicalApproachId = caseSettingsToState(
            preferences,
            state.current.crNumber,
        ).surgicalApproachId;
    }
}

async function saveCase(
    state: SurgicalGuideCaseState,
    id: number,
    canEditCR: boolean,
    config?: AxiosRequestConfig,
): Promise<void> {
    await withStateSaving(state, async () => {
        await saveCaseSettings(id, state.current, canEditCR, config);
        Object.assign(state.lastSaved, cloneDeep(state.current));
    }, config?.signal);
}

export const useSurgicalGuideCase = defineStore('surgical-guide-case-settings', () => {
    const _state = reactive<SurgicalGuideCaseState>(surgicalGuideCaseState());

    const hasUnsavedChanges = computed(() => !isEqual(_state.lastSaved, _state.current));
    const isValid = computed(() => isFormValid(_state.current));
    const isComplete = computed(
        () => _state.current.crNumber !== '' && _state.current.surgicalApproachId !== '',
    );

    function hasError(field: string): boolean {
        if (validators[field] === undefined) {
            throw new Error(`no validator for ${field}`);
        }
        return !validators[field](_state.current);
    }

    return {
        current: _state.current,
        hasError,
        isValid,
        isComplete,
        hasUnsavedChanges,
        $reset: () => resetCaseState(_state),
        loadNewCase: (surgeonId: number, isSingleSurgeon: boolean, config?: AxiosRequestConfig) =>
            loadNewCase(_state, surgeonId, isSingleSurgeon, config),
        loadExistingCase: (id: number, config?: AxiosRequestConfig) =>
            loadExistingCase(_state, id, config),
        onUpdateSurgeon: (surgeonId: number) => updateSurgeon(_state, surgeonId),
        save: (id: number, canEditCR: boolean, config?: AxiosRequestConfig) =>
            saveCase(_state, id, canEditCR, config),
    };
});